import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import * as i1 from '@usana/ux/ssr';
import { UsanaSsrModule } from '@usana/ux/ssr';

const URL_SITE_DOMAIN = new InjectionToken('url.siteDomain');
const URL_FORCED_SUBDOMAIN = new InjectionToken('url.forcedSubdomain', {
    factory: () => '', // nothing forced by default.
});

class HostParts {
}

class UrlService {
    constructor(platformWrappedLocationService, environmentSiteDomain) {
        this.platformWrappedLocationService = platformWrappedLocationService;
        this.environmentSiteDomain = environmentSiteDomain;
        const host = this.platformWrappedLocationService.hostname();
        this.parts = Object.freeze(this.loadHostParts(host));
    }
    /**
     * get the domain url with the current subdomain
     */
    getCurrentHost() {
        return this.getDomainWithSubdomain(this.parts.subdomain);
    }
    /**
     * get the domain url with a given subdomain
     * @param subdomain the subdomain to add to the url
     */
    getDomainWithSubdomain(subdomain) {
        return `https://${subdomain}.${this.parts.domain}`;
    }
    /**
     * get the calculated host parts
     */
    getHostParts() {
        return this.parts;
    }
    getCookieDomain() {
        return this.parts.domain;
    }
    /**
     * get the path portion of the url
     */
    getPath() {
        return this.platformWrappedLocationService
            .href()
            .substring(this.platformWrappedLocationService.href().indexOf(this.parts.domain) + this.parts.domain.length);
    }
    /**
     * assign the browser to the given url
     * @param url url to go to
     */
    goTo(url) {
        this.platformWrappedLocationService.assign(url);
    }
    loadHostParts(host) {
        const hostParts = host.split('.');
        const parts = new HostParts();
        parts.localhost = false;
        parts.https = this.platformWrappedLocationService.protocol() === 'https:';
        if (this.hostIsLocalhost(hostParts)) {
            parts.domain = 'localhost:' + this.platformWrappedLocationService.port();
            parts.subdomain = null;
            parts.localhost = true;
        }
        else {
            // if it ends with the site domain then just make that the domain
            if (host.endsWith(this.environmentSiteDomain)) {
                parts.domain = this.environmentSiteDomain;
            }
            else {
                // everything past the last . but lets cut off everything after the / if it exists
                const domain = hostParts[hostParts.length - 2] + '.' + hostParts[hostParts.length - 1].split('/')[0];
                parts.domain = domain;
            }
            // this means the subdomain is the part before the domain
            parts.subdomain = host.substring(0, host.indexOf('.' + parts.domain));
        }
        return parts;
    }
    hostIsLocalhost(hostParts) {
        return hostParts.length === 1 && hostParts[0].indexOf('localhost') === 0;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: UrlService, deps: [{ token: i1.PlatformWrappedLocationService }, { token: URL_SITE_DOMAIN }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: UrlService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: UrlService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i1.PlatformWrappedLocationService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [URL_SITE_DOMAIN]
                }] }] });

class UrlModule {
    constructor() { }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: UrlModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: UrlModule, imports: [CommonModule, UsanaSsrModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: UrlModule, providers: [UrlService], imports: [CommonModule, UsanaSsrModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: UrlModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, UsanaSsrModule],
                    providers: [UrlService],
                }]
        }], ctorParameters: () => [] });

class UrlCorrectionModule {
    constructor(urlService, platformService, forcedSubdomain) {
        this.urlService = urlService;
        this.platformService = platformService;
        this.forcedSubdomain = forcedSubdomain;
        const hostParts = this.urlService.getHostParts();
        if (this.platformService.isBrowser() && !hostParts.localhost) {
            // if localhost ignore everything.
            if (!!forcedSubdomain && forcedSubdomain !== hostParts.subdomain) {
                // if we have a forced subdomain AND our current subdomain isn't that lets redirect.
                this.goTo(this.urlService.getDomainWithSubdomain(this.forcedSubdomain));
            }
            else if (hostParts.subdomain === '') {
                // if we have no subdomain AND no forced subdomain go to www
                this.goTo(this.urlService.getDomainWithSubdomain('www'));
            }
            else if (!hostParts.https) {
                // if not https force https
                this.goTo(this.urlService.getCurrentHost());
            }
        }
    }
    goTo(url) {
        const path = this.urlService.getPath();
        this.urlService.goTo(`${url}${path}`);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: UrlCorrectionModule, deps: [{ token: UrlService }, { token: i1.PlatformService }, { token: URL_FORCED_SUBDOMAIN }], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: UrlCorrectionModule, imports: [CommonModule, UrlModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: UrlCorrectionModule, imports: [CommonModule, UrlModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: UrlCorrectionModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, UrlModule],
                }]
        }], ctorParameters: () => [{ type: UrlService }, { type: i1.PlatformService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [URL_FORCED_SUBDOMAIN]
                }] }] });

/**
 * Generated bundle index. Do not edit.
 */

export { HostParts, URL_FORCED_SUBDOMAIN, URL_SITE_DOMAIN, UrlCorrectionModule, UrlModule, UrlService };
